import React, { useCallback, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DefaultDialogProps } from 'src/Libs/modals'
import { Formik } from 'formik'
import { NumberField, TextField } from 'src/Libs/inputs/formik'
import { number, object } from 'yup'
import { Grid } from '@mui/material'
import { humanTimeToSecondsFromMidnight, secondsFromMidnightToHumanTime } from '@e3dc-react/shell/Libs/date'
import BooleanToggle from 'src/Libs/inputs/formik/BooleanToggle'
import { MyPvDeviceBoostItemModel } from 'src/Libs/smart-home/models/my-pv-settings.model'
import WeekDaySelector from 'src/Elements/Selectors/WeekDaySelector'
import { Day, Days } from '@e3dc-react/shell/Libs/date/types'
import { UnitTypes } from 'src/Libs/types'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'

interface FormInputType {
  isActive: boolean
  start: string
  end: string
  temperature: number
}

export interface HotWaterSupplyDialogProps extends DefaultDialogProps {
  item: MyPvDeviceBoostItemModel
  onConfirm: (item: MyPvDeviceBoostItemModel, onClose: () => void) => void
}

const TEMPERATURE_MIN = 35
const TEMPERATURE_MAX = 65

const HotWaterSupplyDialog: React.VFC<HotWaterSupplyDialogProps> = ({ item, place, onClose: onDialogClose, onConfirm }) => {
  const [weekDays, setWeekDays] = useState<Days>(item.days)
  const { t } = useCustomTranslation()
  const initialFormValues = {
    isActive: item.isActive,
    start: secondsFromMidnightToHumanTime(item.start),
    end: secondsFromMidnightToHumanTime(item.stop),
    temperature: item.temperature,
  } as FormInputType

  const validationSchema = object().shape({
    temperature: number()
      .min(TEMPERATURE_MIN, t(ValidationMessageKeys.min, { value: TEMPERATURE_MIN }))
      .max(TEMPERATURE_MAX, t(ValidationMessageKeys.max, { value: TEMPERATURE_MAX })),
  })

  const handleSelectDay = useCallback(
    (day: Day, isSelected: boolean) => {
      setWeekDays({ ...weekDays, [day]: isSelected })
    },
    [weekDays]
  )

  const handleConfirm = useCallback(
    (values: FormInputType) => {
      onConfirm(
        {
          isActive: values.isActive,
          start: humanTimeToSecondsFromMidnight(values.start),
          stop: humanTimeToSecondsFromMidnight(values.end),
          temperature: values.temperature,
          name: '',
          days: weekDays,
        },

        onDialogClose
      )
      onDialogClose()
    },
    [onConfirm, onDialogClose, weekDays]
  )

  return (
    <Formik initialValues={initialFormValues} onSubmit={handleConfirm} validationSchema={validationSchema}>
      {({ isValid, dirty, handleSubmit }) => {
        return (
          <Dialog
            title={t('smartHome.hotWaterSupply.dialogTitle')}
            onClose={onDialogClose}
            place={place}
            maxWidth="xs"
            actions={{
              primary: {
                name: t('common.save'),
                disabled: !isValid && !dirty,
                onClick: handleSubmit,
              },
              secondary: [
                {
                  name: t('common.cancel'),
                },
              ],
            }}
          >
            <form onSubmit={handleSubmit}>
              <Grid container>
                <Grid item xs={12}>
                  <BooleanToggle name={'isActive'} trueLabel={t('common.active')} falseLabel={t('common.notActive')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label={t('common.from')} type="time" name={'start'} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label={t('common.until')} type="time" name={'end'} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <NumberField name={'temperature'} label={t('common.temperature')} suffix={UnitTypes.celsius} min={30} max={60} />
                </Grid>
                <Grid item xs={12}>
                  <WeekDaySelector days={weekDays} onSelectDay={handleSelectDay} disabledDays={[]} />
                </Grid>
              </Grid>
            </form>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default HotWaterSupplyDialog
